/** Admin-only card for managing escalation routes.
 *
 * When a reviewer escalates a stage, every matching active route fans
 * the task out to its listed recipients. If no route matches, the
 * backend falls back to all tenant admins so nothing is dropped.
 *
 * Form half is in EscalationRouteForm.tsx (CC-10 200 LOC cap).
 */
import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api, ApiError } from '@/lib/api';
import type {
  EscalationRoute,
  CreateEscalationRouteBody,
} from '@/pages/escalations/types';
import {
  EscalationRouteForm,
  RouteRow,
  type ProfileLite,
  type RouteFormState,
} from './EscalationRouteForm';

const EMPTY_FORM: RouteFormState = {
  name: '',
  description: '',
  match_stage: '',
  match_flag: '',
  recipient_user_ids: [],
  active: true,
};

function bodyFromForm(f: RouteFormState): CreateEscalationRouteBody {
  return {
    name: f.name.trim(),
    description: f.description.trim() || null,
    match_stage: f.match_stage || null,
    match_flag: f.match_flag.trim() || null,
    recipient_user_ids: f.recipient_user_ids,
    active: f.active,
  };
}

export function EscalationRoutesCard() {
  const [routes, setRoutes] = useState<EscalationRoute[]>([]);
  const [users, setUsers] = useState<ProfileLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RouteFormState>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, u] = await Promise.all([
        api.get<{ data: EscalationRoute[] }>('/api/v1/escalation-routes'),
        // GET /api/v1/users/ returns the array verbatim — no { data } wrapper.
        api.get<ProfileLite[]>('/api/v1/users/'),
      ]);
      setRoutes(r.data);
      setUsers(u);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Load failed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function startCreate() {
    setEditingId('__new__');
    setForm(EMPTY_FORM);
  }

  function startEdit(r: EscalationRoute) {
    setEditingId(r.id);
    setForm({
      name: r.name,
      description: r.description ?? '',
      match_stage: r.match_stage ?? '',
      match_flag: r.match_flag ?? '',
      recipient_user_ids: r.recipient_user_ids,
      active: r.active,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function save() {
    if (!form.name.trim() || form.recipient_user_ids.length === 0) {
      setError('Name and at least one recipient are required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (editingId && editingId !== '__new__') {
        await api.put(`/api/v1/escalation-routes/${editingId}`, bodyFromForm(form));
      } else {
        await api.post('/api/v1/escalation-routes', bodyFromForm(form));
      }
      cancelEdit();
      await load();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this route?')) return;
    setBusy(true);
    try {
      await api.delete(`/api/v1/escalation-routes/${id}`);
      await load();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  }

  const userById = new Map(users.map((u) => [u.id, u]));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span>Escalation routing</span>
          {!editingId && (
            <Button size="sm" variant="outline" onClick={startCreate} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add route
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          When a reviewer escalates a stage, every matching route fans the task
          out to its listed recipients. If no route matches, the task goes to
          all admins as a fallback.
        </p>
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}
        {loading ? (
          <p className="text-sm text-muted-foreground py-3">Loading…</p>
        ) : (
          <>
            {routes.map((r) => (
              <RouteRow
                key={r.id}
                r={r}
                userById={userById}
                busy={busy}
                onEdit={() => { startEdit(r); }}
                onDelete={() => { void remove(r.id); }}
              />
            ))}
            {routes.length === 0 && (
              <p className="text-sm text-muted-foreground py-3">
                No routes configured yet. Add one to start routing escalations.
              </p>
            )}
          </>
        )}

        {editingId && (
          <EscalationRouteForm
            isNew={editingId === '__new__'}
            users={users}
            form={form}
            setForm={setForm}
            busy={busy}
            onCancel={cancelEdit}
            onSave={() => { void save(); }}
          />
        )}
      </CardContent>
    </Card>
  );
}
